// hooks/useReviews.js
//
// TanStack Query hooks for the review moderation flow. Mirrors
// hooks/useStatements.js — keys exported for cache consistency,
// useQuery for reads, useMutation for the hide/unhide write that
// invalidates touched keys.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    getAdminReviewById,
    getVendorPortalReviews,
    listAdminDeliveryReviews,
    listAdminOrderReviews,
    listAdminProductReviews,
    listAdminReviews,
    listAdminVendorReviews,
    recomputeAllReviewAggregates,
    setReviewStatus,
} from "services/reviews";

export const reviewKeys = {
    all: ["reviews"],
    list: (params) => [...reviewKeys.all, "list", { params }],
    detail: (id) => [...reviewKeys.all, "detail", id],
    target: (targetType, targetId, params) => [
        ...reviewKeys.all,
        "target",
        targetType,
        targetId,
        { params },
    ],
    order: (orderId) => [...reviewKeys.all, "order", orderId],
    vendorPortal: (params) => [...reviewKeys.all, "vendor-portal", { params }],
};

/**
 * All reviews on a single order (any user, any target type). Used by the
 * admin OrderDetails page to render a "Reviews for this order" panel.
 *
 * `enabled: false` makes no call. The API refuses this route for a user who
 * may not manage reviews, and each refusal is an entry in the audit trail:
 * the order page of such users made 60 to 100 of them each day.
 *
 * @param {number|string} orderId
 * @param {{enabled?: boolean}} [options]
 */
export const useListOrderReviews = (orderId, { enabled = true } = {}) => {
    const query = useQuery({
        queryKey: reviewKeys.order(orderId),
        queryFn: () => listAdminOrderReviews(orderId),
        enabled: Boolean(orderId) && enabled,
    });

    return {
        reviews: query.data?.reviews ?? [],
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
    };
};

/**
 * Admin moderation queue list. Filter params: status, targetType,
 * targetId, minRating, maxRating, search, page, limit.
 */
export const useListAdminReviews = (params = {}) => {
    const query = useQuery({
        queryKey: reviewKeys.list(params),
        queryFn: () => listAdminReviews(params),
        keepPreviousData: true,
    });

    return {
        reviews: query.data?.reviews ?? [],
        totalCount: query.data?.totalCount ?? 0,
        totalPages: query.data?.totalPages ?? 0,
        page: query.data?.page ?? 1,
        summary: query.data?.summary ?? null,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

export const useGetAdminReview = (id) => {
    const query = useQuery({
        queryKey: reviewKeys.detail(id),
        queryFn: () => getAdminReviewById(id),
        enabled: Boolean(id),
    });

    return {
        review: query.data?.review ?? null,
        loading: query.isLoading,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/**
 * Hide / unhide a single review. On success invalidates the moderation
 * list and the single-review detail keys.
 */
export const useSetReviewStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, status }) => setReviewStatus(id, status),
        onSuccess: (_data, { id }) => {
            queryClient.invalidateQueries({ queryKey: reviewKeys.all });
            queryClient.invalidateQueries({ queryKey: reviewKeys.detail(id) });
        },
    });
};

/**
 * Per-target admin feed — used by the vendor / product / user detail
 * pages' "Reviews" tab.
 */
export const useListTargetReviews = (targetType, targetId, params = {}) => {
    const query = useQuery({
        queryKey: reviewKeys.target(targetType, targetId, params),
        queryFn: () => {
            if (targetType === "vendor") return listAdminVendorReviews(targetId, params);
            if (targetType === "product") return listAdminProductReviews(targetId, params);
            if (targetType === "delivery") return listAdminDeliveryReviews(targetId, params);
            throw new Error(`unknown target type: ${targetType}`);
        },
        enabled: Boolean(targetType && targetId),
        keepPreviousData: true,
    });

    return {
        reviews: query.data?.reviews ?? [],
        totalCount: query.data?.totalCount ?? 0,
        totalPages: query.data?.totalPages ?? 0,
        page: query.data?.page ?? 1,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
    };
};

/**
 * Super-admin-only: trigger a full recompute of every aggregate. Returns
 * the counts of touched targets. Invalidates the review caches so any
 * downstream display picks up the new numbers.
 */
export const useRecomputeAllReviewAggregates = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: recomputeAllReviewAggregates,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: reviewKeys.all });
        },
    });
};

/**
 * Vendor-role-only feed: the logged-in vendor's reviews across their
 * vendor profile, products, and drivers. Server enforces the role check.
 */
export const useVendorPortalReviews = (params = {}) => {
    const query = useQuery({
        queryKey: reviewKeys.vendorPortal(params),
        queryFn: () => getVendorPortalReviews(params),
        keepPreviousData: true,
    });

    return {
        reviews: query.data?.reviews ?? [],
        ratingAvg: query.data?.ratingAvg ?? null,
        ratingCount: query.data?.ratingCount ?? 0,
        totalCount: query.data?.totalCount ?? 0,
        totalPages: query.data?.totalPages ?? 0,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};
